import React, { FormEvent } from 'react';
import { CHATBOT_EVENTS } from 'src/lib/trackingEvents';
import { QuestionObject, Source, HistoryEntry, Speaker } from './types';
import {
  WAITING_MESSAGE_TIME,
  WAITING_MESSAGE_TIME_LONG,
  TIMEOUT_ERROR_TIME,
  WAITING_MESSAGES,
} from './constants';

interface ChatStateType {
  // UI state
  setCurrentAnswer: (
    updater: ((prevAnswer: string) => string) | string,
  ) => void;
  setWaitingMessage: (message: string) => void;
  setIsStreaming: (isStreaming: boolean) => void;
  setAnyError: (hasError: boolean) => void;
  setAnswerFeedback: (feedback: string) => void;
  answerFeedback: string;
  setAnswerSources: (sources: Source[]) => void;
  setLastQuestion: (question: string) => void;
  setCurrentQuestion: (question: string) => void;
  currentQuestion: string;

  // WebSocket state
  finnServerRef: React.MutableRefObject<string>;
  finnServer: string;
  setFinnServer: (server: string) => void;
  currentWebsocket: React.MutableRefObject<WebSocket | null>;
  isStreamingRef: React.MutableRefObject<boolean>;
  pendingQuestion: React.MutableRefObject<QuestionObject | null>;
  hasStreamingStarted: React.MutableRefObject<boolean>;
  lastQuestionID: React.MutableRefObject<string>;
  addAnswerToHistory: React.MutableRefObject<() => void>;
  timeQuestionSubmitted: React.MutableRefObject<number>;
  chatHistory: Array<HistoryEntry>;
  addItemToHistory: (speaker: Speaker, content: string) => void;
  generateQuestionId: () => string;
  setChatHistory: (updater: React.SetStateAction<HistoryEntry[]>) => void;

  // User state
  userId: React.MutableRefObject<string>;
  anonId: React.MutableRefObject<string>;

  // Docs-section hint sent with each question
  section: string;

  // Timeout state
  smallWaitTimeoutId: React.MutableRefObject<NodeJS.Timeout | null>;
  patienceTimeoutId: React.MutableRefObject<NodeJS.Timeout | null>;
  serviceTimeoutId: React.MutableRefObject<NodeJS.Timeout | null>;
  resetTimeouts: () => void;
}

interface AnalyticsType {
  trackQuestionAsked: (question: string) => void;
  trackFeedbackSubmitted: (feedback: string) => void;
  trackEventWithTiming: (event: string) => void;
  trackErrorWithOptionalCode: (errorCode?: string) => void;
}

// Define interfaces for websocket messages
interface BaseMessage {
  type: string;
  qID: string;
}

interface SourcesMessage extends BaseMessage {
  type: 'sources';
  sources: Array<Source>;
}

interface AnswerMessage extends BaseMessage {
  type: 'answer';
  ans: string;
}

interface StatusMessage extends BaseMessage {
  type: 'status';
  status: string;
}

interface ErrorMessage extends BaseMessage {
  type: 'error';
  errorCode?: string;
}

// Union type of all possible message types
type FinnMessage =
  | SourcesMessage
  | AnswerMessage
  | StatusMessage
  | ErrorMessage
  | BaseMessage;

interface ChatMessageHandlersProps {
  chatState: ChatStateType;
  analytics: AnalyticsType;
}

/**
 * Hook for handling error display and timeouts
 */
export function useErrorHandlers({
  chatState,
  analytics,
}: ChatMessageHandlersProps) {
  // Display an error message in the chat
  const displayError = (errorMessage: string, errorCode?: string) => {
    chatState.setCurrentAnswer((prevStream) => {
      return prevStream + errorMessage;
    });
    chatState.addAnswerToHistory.current();
    chatState.setIsStreaming(false);
    analytics.trackErrorWithOptionalCode(errorCode);
    chatState.setAnyError(true);
  };

  // Set up timeouts for waiting messages
  const activateTimeouts = () => {
    chatState.setWaitingMessage('');

    chatState.smallWaitTimeoutId.current = setTimeout(() => {
      chatState.setWaitingMessage(
        WAITING_MESSAGES[Math.floor(Math.random() * WAITING_MESSAGES.length)] ??
          "Hang tight! We'll have an answer soon.",
      );
    }, WAITING_MESSAGE_TIME) as unknown as NodeJS.Timeout;

    chatState.patienceTimeoutId.current = setTimeout(() => {
      chatState.setWaitingMessage(
        'Sorry! Bill is taking longer than usual! Please be patient.',
      );
    }, WAITING_MESSAGE_TIME_LONG) as unknown as NodeJS.Timeout;

    // Change message if service times out (e.g., after 30 seconds)
    chatState.serviceTimeoutId.current = setTimeout(() => {
      displayError(
        'Sorry -- Bill seems to be taking some unscheduled PTO. Please try again later.',
        'clientTimeout',
      );
    }, TIMEOUT_ERROR_TIME) as unknown as NodeJS.Timeout;
  };

  return {
    displayError,
    activateTimeouts,
  };
}

/**
 * Hook for handling WebSocket communication
 */
export function useWebSocketHandlers({
  chatState,
  analytics,
}: ChatMessageHandlersProps) {
  const { displayError } = useErrorHandlers({ chatState, analytics });

  // Send feedback to the server
  const sendFeedbackToServer = (feedback: string) => {
    if (chatState.currentWebsocket.current !== null) {
      chatState.currentWebsocket.current.send(
        JSON.stringify({
          type: 'feedback',
          question_id: chatState.lastQuestionID.current,
          feedback,
        }),
      );
    }
  };

  // Handle feedback from the user
  const handleFeedback = (feedbackString: string) => {
    // Let the user "unset" their feedback by clicking the same button again
    const feedback =
      feedbackString === chatState.answerFeedback ? 'neutral' : feedbackString;

    chatState.setAnswerFeedback(feedback);
    analytics.trackFeedbackSubmitted(feedback);
    sendFeedbackToServer(feedback);
  };

  // Process incoming message from the finn server
  const handleFinnMessage = (message: FinnMessage) => {
    if (!message.hasOwnProperty('type')) {
      console.error(`Unknown message format received`);
      return;
    }

    if (message.qID !== chatState.lastQuestionID.current.substring(0, 8)) {
      return;
    }

    switch (message.type) {
      case 'sources':
        if ('sources' in message) {
          const sourcesMessage = message as SourcesMessage;
          chatState.setAnswerSources(
            sourcesMessage.sources.map(({ title, url }) => ({ title, url })),
          );
          analytics.trackEventWithTiming(CHATBOT_EVENTS.SOURCES_RECEIVED);
        }
        break;

      case 'answer':
        chatState.resetTimeouts();
        if ('ans' in message) {
          const answerMessage = message as AnswerMessage;
          if (!chatState.isStreamingRef.current) {
            // Ignore answers if we're not streaming
            return;
          }
          if (!chatState.hasStreamingStarted.current) {
            chatState.hasStreamingStarted.current = true;
            analytics.trackEventWithTiming(
              CHATBOT_EVENTS.FIRST_RESPONSE_RECEIVED,
            );
          }
          chatState.setCurrentAnswer((prevStream) => {
            return prevStream + answerMessage.ans;
          });
        }
        break;

      case 'status':
        if ('status' in message) {
          const statusMessage = message as StatusMessage;
          if (statusMessage.status === 'finished') {
            chatState.setIsStreaming(false);
            chatState.addAnswerToHistory.current();
            analytics.trackEventWithTiming(CHATBOT_EVENTS.QUESTION_FINISHED);
          } else if (statusMessage.status === 'thinking') {
            // Finn is thinking!
          } else {
            console.error(`Unknown message status: ${statusMessage.status}`);
          }
        }
        break;

      case 'error':
        if (chatState.isStreamingRef.current) {
          const errorMessage = message as ErrorMessage;
          let errorText =
            ' Sorry! Bill is having trouble answering this question. Please try again later.';
          if (errorMessage?.errorCode === 'limit') {
            errorText =
              ' Sorry! Bill is overworked, and we here at Plaid believe in a positive work-life balance, even for our chatbots. Please try again later.';
          }
          displayError(errorText, errorMessage?.errorCode);
        }
        break;

      default:
        console.error(`Unknown message type: ${message.type}`);
    }
  };

  // Process incoming websocket message
  const handleWebsocketMessage = (event: MessageEvent<string>) => {
    const eventObj = JSON.parse(event.data) as FinnMessage;
    handleFinnMessage(eventObj);
  };

  // Handle websocket close event
  const handleWebsocketClose = (_event: CloseEvent) => {
    // No action needed
  };

  // Handle websocket open event
  const handleWebsocketOpen = (_event: Event) => {
    if (chatState.pendingQuestion.current != null) {
      chatState.currentWebsocket.current?.send(
        JSON.stringify(chatState.pendingQuestion.current),
      );
    }
    chatState.pendingQuestion.current = null;
  };

  // Initialize websocket with a question
  const initializeWebsocketWithQuestion = (
    questionObjectLocal: QuestionObject,
  ) => {
    if (
      chatState.currentWebsocket.current !== null &&
      chatState.currentWebsocket.current.readyState === WebSocket.OPEN
    ) {
      // Reusing existing socket
      chatState.currentWebsocket.current.send(
        JSON.stringify(questionObjectLocal),
      );
    } else {
      // Close any existing connection first
      if (chatState.currentWebsocket.current !== null) {
        try {
          chatState.currentWebsocket.current.close();
        } catch (e) {
          console.error('Error closing websocket:', e);
        }
      }

      chatState.pendingQuestion.current = questionObjectLocal;

      try {
        if (!chatState.finnServerRef.current) {
          console.error('Empty server URL!');
          displayError(
            'Server configuration error. Please try again later.',
            'emptyServerUrl',
          );
          return;
        }

        chatState.currentWebsocket.current = new WebSocket(
          chatState.finnServerRef.current,
        );

        chatState.currentWebsocket.current.addEventListener(
          'message',
          handleWebsocketMessage,
        );
        chatState.currentWebsocket.current.addEventListener(
          'close',
          handleWebsocketClose,
        );
        chatState.currentWebsocket.current.addEventListener(
          'open',
          handleWebsocketOpen,
        );

        // Add error listener
        chatState.currentWebsocket.current.addEventListener(
          'error',
          (event) => {
            console.error('WebSocket error:', event);
            displayError(
              'Connection error. Please try again later.',
              'websocketError',
            );
          },
        );
      } catch (e) {
        console.error('Error creating websocket:', e);
        displayError(
          'Failed to connect to chat service. Please try again later.',
          'connectionError',
        );
      }
    }
  };

  return {
    sendFeedbackToServer,
    handleFeedback,
    handleFinnMessage,
    handleWebsocketMessage,
    handleWebsocketClose,
    handleWebsocketOpen,
    initializeWebsocketWithQuestion,
  };
}

/**
 * Hook for handling form interactions and server selection
 */
export function useFormHandlers({
  chatState,
  analytics,
}: ChatMessageHandlersProps) {
  const { activateTimeouts } = useErrorHandlers({ chatState, analytics });
  const { initializeWebsocketWithQuestion } = useWebSocketHandlers({
    chatState,
    analytics,
  });

  // Stop the current stream
  const handleStopStream = () => {
    chatState.resetTimeouts();
    chatState.setWaitingMessage('');

    if (chatState.currentWebsocket.current !== null) {
      chatState.addAnswerToHistory.current();
      chatState.currentWebsocket.current.send(
        JSON.stringify({ type: 'stop', stop: true }),
      );
      chatState.setIsStreaming(false);
      analytics.trackEventWithTiming(CHATBOT_EVENTS.USER_CLICKED_STOP);
      chatState.lastQuestionID.current = '';
    }
  };

  // Submit a question to the chat
  const submitQuestion = (question: string) => {
    if (question === '') {
      return;
    }

    chatState.generateQuestionId();
    analytics.trackQuestionAsked(question);
    chatState.setAnyError(false);

    initializeWebsocketWithQuestion({
      type: 'question',
      question: question,
      question_id: chatState.lastQuestionID.current,
      chat_history: chatState.chatHistory,
      user_id: chatState.userId.current,
      anonymous_id: chatState.anonId.current,
      section: chatState.section,
    });

    chatState.timeQuestionSubmitted.current = new Date().getTime();
    chatState.hasStreamingStarted.current = false;
    chatState.setIsStreaming(true);
    activateTimeouts();
    chatState.addItemToHistory('user', question);
    chatState.setCurrentAnswer('');
    chatState.setAnswerFeedback('neutral');
    chatState.setAnswerSources([]);
  };

  // Handle form submission
  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!chatState.isStreamingRef.current) {
      submitQuestion(chatState.currentQuestion);
      chatState.setLastQuestion(chatState.currentQuestion);
      chatState.setCurrentQuestion('');
    }
  };

  // Handle server selection
  const handleServerSelected = (newServer: string) => {
    chatState.setCurrentAnswer('');
    chatState.setLastQuestion('');
    handleStopStream();
    chatState.currentWebsocket.current = null;
    // Clear the chat history when you switch servers
    chatState.setChatHistory([]);
    chatState.setFinnServer(newServer);
  };

  return {
    handleStopStream,
    submitQuestion,
    handleSubmit,
    handleServerSelected,
  };
}

/**
 * Main hook that combines all message handlers
 */
export function useChatMessageHandlers({
  chatState,
  analytics,
}: ChatMessageHandlersProps) {
  const errorHandlers = useErrorHandlers({ chatState, analytics });
  const webSocketHandlers = useWebSocketHandlers({ chatState, analytics });
  const formHandlers = useFormHandlers({ chatState, analytics });

  return {
    ...errorHandlers,
    ...webSocketHandlers,
    ...formHandlers,
  };
}
